/**
 * Client preferences: localStorage + export/import.
 * Never store passwords.
 */

export const PREFS_KEY = "mdhoria.prefs.v1";
/** Bump when applying one-time migrations in normalizePrefs. */
export const PREFS_VERSION = 2;
export const HISTORY_MAX = 200;
export const CLIENT_BUILD = "1b.1";

/** Classic direction shortcuts — expand only on Send/Enter, never on keydown. */
export const DEFAULT_DIR_ALIASES: Record<string, string> = {
  n: "north",
  s: "south",
  e: "east",
  w: "west",
  u: "up",
  d: "down",
};

export type FontSize = "small" | "medium" | "large";

export interface CharPrefs {
  aliases: Record<string, string>;
  history: string[];
}

export interface ClientPrefs {
  version: number;
  wsUrl: string;
  autoReconnect: boolean;
  fontSize: FontSize;
  highContrast: boolean;
  exitHotkeys: boolean;
  showTimestamps: boolean;
  /** Active profile name for aliases/history (not the mud password). */
  activeProfile: string;
  /** Global aliases (always applied; profile aliases override same key). */
  globalAliases: Record<string, string>;
  characters: Record<string, CharPrefs>;
}

const DEFAULT_PROFILE = "default";

export function defaultWsUrl(): string {
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${location.host}/ws`;
}

export function emptyCharPrefs(): CharPrefs {
  return { aliases: {}, history: [] };
}

export function defaultPrefs(): ClientPrefs {
  return {
    version: PREFS_VERSION,
    wsUrl: defaultWsUrl(),
    autoReconnect: true,
    fontSize: "medium",
    highContrast: false,
    // Arrow-key exits only when enabled; letter keys must never steal login typing.
    exitHotkeys: false,
    showTimestamps: false,
    activeProfile: DEFAULT_PROFILE,
    globalAliases: { ...DEFAULT_DIR_ALIASES },
    characters: {
      [DEFAULT_PROFILE]: emptyCharPrefs(),
    },
  };
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function sanitizeAliases(raw: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (!isRecord(raw)) return out;
  for (const [k, v] of Object.entries(raw)) {
    const key = k.trim().toLowerCase();
    if (!key || key.includes(" ") || typeof v !== "string") continue;
    const exp = v.trim();
    if (!exp) continue;
    // modest size caps
    if (key.length > 32 || exp.length > 200) continue;
    out[key] = exp;
  }
  return out;
}

function sanitizeHistory(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((x): x is string => typeof x === "string" && x.length > 0 && x.length < 500)
    .slice(-HISTORY_MAX);
}

function sanitizeChar(raw: unknown): CharPrefs {
  if (!isRecord(raw)) return emptyCharPrefs();
  return {
    aliases: sanitizeAliases(raw.aliases),
    history: sanitizeHistory(raw.history),
  };
}

/** Migrate / validate arbitrary JSON into ClientPrefs. */
export function normalizePrefs(raw: unknown): ClientPrefs {
  const base = defaultPrefs();
  if (!isRecord(raw)) return base;

  const priorVersion =
    typeof raw.version === "number" && Number.isFinite(raw.version)
      ? raw.version
      : 1;

  const p: ClientPrefs = {
    version: PREFS_VERSION,
    wsUrl:
      typeof raw.wsUrl === "string" && raw.wsUrl.trim()
        ? raw.wsUrl.trim()
        : base.wsUrl,
    autoReconnect: raw.autoReconnect !== false,
    fontSize:
      raw.fontSize === "small" || raw.fontSize === "large" || raw.fontSize === "medium"
        ? raw.fontSize
        : "medium",
    highContrast: raw.highContrast === true,
    // Default off: letter hotkeys blocked login (e.g. name starting with S).
    exitHotkeys: raw.exitHotkeys === true,
    showTimestamps: raw.showTimestamps === true,
    activeProfile:
      typeof raw.activeProfile === "string" && raw.activeProfile.trim()
        ? raw.activeProfile.trim().slice(0, 32)
        : DEFAULT_PROFILE,
    globalAliases: sanitizeAliases(raw.globalAliases),
    characters: {},
  };

  // v1 → v2: disable instant letter exits; seed n/s/e/w/u/d aliases if missing.
  if (priorVersion < 2) {
    p.exitHotkeys = false;
    for (const [k, v] of Object.entries(DEFAULT_DIR_ALIASES)) {
      if (p.globalAliases[k] === undefined) p.globalAliases[k] = v;
    }
  }

  if (isRecord(raw.characters)) {
    for (const [name, cp] of Object.entries(raw.characters)) {
      const n = name.trim().slice(0, 32);
      if (!n) continue;
      p.characters[n] = sanitizeChar(cp);
    }
  }

  // Legacy single-alias blob (if any)
  if (Object.keys(p.characters).length === 0 && isRecord(raw.aliases)) {
    p.characters[DEFAULT_PROFILE] = {
      aliases: sanitizeAliases(raw.aliases),
      history: sanitizeHistory(raw.history),
    };
  }

  if (!p.characters[p.activeProfile]) {
    p.characters[p.activeProfile] = emptyCharPrefs();
  }

  // Migrate old key mdhoria.wsUrl if present and prefs empty-ish
  try {
    const oldUrl = localStorage.getItem("mdhoria.wsUrl");
    if (oldUrl && (!raw.wsUrl || raw.wsUrl === base.wsUrl)) {
      p.wsUrl = oldUrl;
    }
  } catch {
    /* ignore */
  }

  return p;
}

export function loadPrefs(): ClientPrefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) {
      // migrate lone ws url
      const p = defaultPrefs();
      try {
        const old = localStorage.getItem("mdhoria.wsUrl");
        if (old) p.wsUrl = old;
      } catch {
        /* ignore */
      }
      return p;
    }
    return normalizePrefs(JSON.parse(raw) as unknown);
  } catch {
    return defaultPrefs();
  }
}

export function savePrefs(prefs: ClientPrefs): void {
  const clean = normalizePrefs(prefs);
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(clean));
  } catch (e) {
    console.warn("Could not save prefs", e);
  }
}

export function exportPrefsJson(prefs: ClientPrefs): string {
  const clean = normalizePrefs(prefs);
  return JSON.stringify(clean, null, 2);
}

export function importPrefsJson(text: string): ClientPrefs {
  const data = JSON.parse(text) as unknown;
  return normalizePrefs(data);
}

export function ensureProfile(prefs: ClientPrefs, name: string): CharPrefs {
  const n = name.trim().slice(0, 32) || DEFAULT_PROFILE;
  if (!prefs.characters[n]) {
    prefs.characters[n] = emptyCharPrefs();
  }
  return prefs.characters[n]!;
}

export function getActiveChar(prefs: ClientPrefs): CharPrefs {
  return ensureProfile(prefs, prefs.activeProfile);
}

/**
 * Expand client aliases. First word (case-insensitive) match only.
 * Profile aliases override global. Does not expand in password mode (caller).
 * Returns { line, expanded } where expanded is true if changed.
 */
export function expandAlias(
  line: string,
  prefs: ClientPrefs
): { line: string; expanded: boolean; from?: string } {
  const trimmed = line.trim();
  if (!trimmed) return { line, expanded: false };

  const sp = trimmed.indexOf(" ");
  const head = (sp === -1 ? trimmed : trimmed.slice(0, sp)).toLowerCase();
  const rest = sp === -1 ? "" : trimmed.slice(sp + 1);

  const charPrefs = getActiveChar(prefs);
  const exp = charPrefs.aliases[head] ?? prefs.globalAliases[head];
  if (!exp) return { line, expanded: false };

  const out = rest ? `${exp} ${rest}` : exp;
  return { line: out, expanded: true, from: head };
}

export function pushHistory(prefs: ClientPrefs, command: string): void {
  const ch = getActiveChar(prefs);
  if (!command || command.length > 500) return;
  if (ch.history[ch.history.length - 1] === command) return;
  ch.history.push(command);
  if (ch.history.length > HISTORY_MAX) {
    ch.history = ch.history.slice(-HISTORY_MAX);
  }
}

export function listProfiles(prefs: ClientPrefs): string[] {
  return Object.keys(prefs.characters).sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: "base" })
  );
}

/** Format aliases as editable text: one "key expansion" per line. */
export function aliasesToText(aliases: Record<string, string>): string {
  return Object.keys(aliases)
    .sort()
    .map((k) => `${k} ${aliases[k]}`)
    .join("\n");
}

/** Parse editable alias text into a map. */
export function textToAliases(text: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const line of text.split(/\r?\n/)) {
    const t = line.trim();
    if (!t || t.startsWith("#")) continue;
    const sp = t.indexOf(" ");
    if (sp === -1) continue;
    const key = t.slice(0, sp).trim().toLowerCase();
    const exp = t.slice(sp + 1).trim();
    if (!key || !exp || key.includes(" ")) continue;
    if (key.length > 32 || exp.length > 200) continue;
    out[key] = exp;
  }
  return out;
}
